import { buildCityScoresContent } from './pageBuilders/cityScores'
import { buildConsiderationComparisonContent } from './pageBuilders/considerationComparison'
import { buildDashboardContent } from './pageBuilders/dashboard'
import { buildDimensionComparisonContent } from './pageBuilders/dimensionComparison'
import { buildIndicatorComparisonContent } from './pageBuilders/indicatorComparison'
import { buildSdgContent } from './pageBuilders/sdg'
import { t } from './translate'

interface PageContext {
  name: string
  content: Record<string, unknown>
}

export interface StoreData {
  isMultiCity: boolean
  selectedQuarter: string | null
  locale?: string
  // Single-city data
  allInfo?: {
    LatestCityIq: number
    LatestCityStar: number
    LatestCityDimensions: { id: number; name: string; value: number }[]
    LatestCityConsiderations: { id: number; name: string; value: number }[]
    LatestCityIndicators: {
      id: number
      name: string
      value: number
      measure: string
    }[]
    LatestCityIndicatorWeights: { i: number; c: number; w: number }[]
  }
  // Consideration comparison chart data (single-city)
  considerationsComparison?: {
    id: number
    curr: number | null
    prev_1: number | null
    prev_2?: number | null
    prev_3?: number | null
  }[]
  selectedDimensionId?: number
  // Multi-city data
  multiCityInfos?: Record<
    string,
    {
      city_id: number
      iq: number
      iq_quality_star: number
      dimensions: { id: number; name: string; value: number }[]
    }
  >
  cityCountryMap?: {
    obj: Record<string, { id: number; name: string; country: string }>
  }
  filteredCities?: { id: number; name: string }[]
  // Drawer state (for city-scores page)
  drawerOpen?: boolean
  drawerConsideration?: number | string
  // SDG data (from CityStore)
  sdg?: {
    previousQuarter?: {
      changePercent: { id: number; value: number }[]
      graphTitle: string
    }
    previousYear?: {
      changePercent: { id: number; value: number }[]
      graphTitle: string
    }
  } | null
  // Dimension comparison trend data
  dimensionsComparison?: {
    snapshot: string
    dimension1: number
    dimension2: number
    dimension3: number
    dimension4: number
    dimension5: number
    milestone: number
  }[]
  // Milestones
  milestones?: { id: number; title: string; date: string }[]
  // Indicator chart filters (from PageStore)
  indicatorChartFilters?: {
    selectedDimension: number
    selectedConsideration: number
    searchKey: string
    isShowingPrevQuarters: boolean
  }
  // Indicator chart data (from PageStore)
  indicatorChartData?: {
    indicatorWeights: { i: number; c: number; w: number }[] | null
    indicators: { id: number; value: number; name?: string }[] | null
    year: number
    quarter: number
  }[]
}

export const DIMENSION_NAMES: Record<number, string> = {
  1: 'Basic Needs',
  2: 'Competitiveness',
  3: 'Opportunity',
  4: 'Livability',
  5: 'Destiny',
}

export const getDimensionName = (
  id: number,
  fallback?: string,
  locale?: string
): string => {
  if (locale) {
    return t(`dimensions.${id}`, locale)
  }
  return (
    fallback ??
    (DIMENSION_NAMES as Partial<Record<number, string>>)[id] ??
    `Dimension ${id}`
  )
}

export const getConsiderationName = (
  id: number,
  fallback?: string,
  locale?: string
): string => {
  if (locale) {
    return t(`considerations.${id}`, locale)
  }
  return fallback ?? `Consideration ${id}`
}

export const getSdgName = (id: number, locale?: string): string => {
  if (locale) {
    return t(`sdg.item.${id}`, locale)
  }
  return `SDG ${id}`
}

const PAGE_BUILDERS: Record<
  string,
  { name: string; buildContent: (data?: StoreData) => Record<string, unknown> }
> = {
  dashboard: { name: 'Dashboard', buildContent: buildDashboardContent },
  'city-scores': { name: 'City Scores', buildContent: buildCityScoresContent },
  'city-profile': {
    name: 'City Profile',
    buildContent: () => ({
      description: 'Map view showing the selected city location.',
    }),
  },
  sdg: {
    name: 'Sustainable Development Goals',
    buildContent: buildSdgContent,
  },
  'dimension-comparison': {
    name: 'Dimension Comparison',
    buildContent: buildDimensionComparisonContent,
  },
  'consideration-comparison': {
    name: 'Consideration Comparison',
    buildContent: buildConsiderationComparisonContent,
  },
  'indicator-comparison': {
    name: 'Indicator Comparison',
    buildContent: buildIndicatorComparisonContent,
  },
  methodology: {
    name: 'Methodology',
    buildContent: () => ({
      description:
        'Information page explaining the CitiIQ methodology. Sections include: CitiIQ Hierarchy and Algorithm, Weighting the Data, Data Quality Rating, SDG Compatibility and Tracker, and Indicators.',
    }),
  },
}

export const getPageContext = (
  page: string,
  storeData?: StoreData
): PageContext | null => {
  const builder = PAGE_BUILDERS[page] as
    | {
        name: string
        buildContent: (data?: StoreData) => Record<string, unknown>
      }
    | undefined
  if (!builder) return null

  return {
    name: builder.name,
    content: builder.buildContent(storeData),
  }
}
